import { useEffect, useState } from 'react';
import { CheckCircle, AlertCircle, ExternalLink, Settings, X, Eye, EyeOff, Save, Loader2, Zap, Lock } from 'lucide-react';
import { getConnectors, saveConnectorConfig, deleteConnectorConfig } from '../services/prospectingService';
import type { ConnectorDef, ConnectorSelection } from '../services/prospectingService';
import { useAuthStore } from '@/hooks/useAuthStore';

const CATEGORY_ORDER = ['DISCOVERY', 'ENRICHMENT', 'LLM', 'EMAIL', 'LINKEDIN', 'WEBHOOK'] as const;

// Pipeline stage mapping — maps each connector slot to its role in the 5-agent pipeline
// and which of the 14 ABM journey stages it feeds intelligence into.
const PIPELINE_STAGE: Record<string, {
  step: number; label: string; icon: string;
  agentName: string; abmStages: string; desc: string;
}> = {
  DISCOVERY:  { step:1, label:'Discovery',   icon:'🔍', agentName:'Researcher Agent',       abmStages:'ABM Stage 1',         desc:'Find matching companies by industry, region & buying signals' },
  ENRICHMENT: { step:2, label:'Enrichment',   icon:'🏢', agentName:'Pain Analyst Agent',     abmStages:'ABM Stages 2–5',      desc:'Pain axis, maturity band, ICP score, firmographics' },
  LLM:        { step:3, label:'Strategy & Outreach', icon:'🤖', agentName:'Strategist + Outreach Agents', abmStages:'ABM Stages 6–14', desc:'Win themes, use cases, email/call/LinkedIn copy' },
  EMAIL:      { step:4, label:'Contacts',     icon:'📧', agentName:'Prospect Finder Agent',  abmStages:'ABM Stages 1,6,12,14',desc:'Email, direct phone, LinkedIn for key contacts' },
  LINKEDIN:   { step:5, label:'LinkedIn',     icon:'🔗', agentName:'LinkedIn Enrichment',    abmStages:'ABM Stage 1',         desc:'LinkedIn profile enrichment for contacts' },
  WEBHOOK:    { step:6, label:'Webhook',      icon:'⚡', agentName:'Custom Integration',     abmStages:'All stages',          desc:'Push pipeline results to your CRM or Slack' },
};
const CATEGORY_TO_SLOT: Record<string, keyof ConnectorSelection> = {
  DISCOVERY: 'discovery', ENRICHMENT: 'enrichment', EMAIL: 'email',
  LLM: 'llm', LINKEDIN: 'linkedin', WEBHOOK: 'webhook',
};

interface Props {
  selection: ConnectorSelection;
  onChange: (s: ConnectorSelection) => void;
}

export default function ConnectorSelector({ selection, onChange }: Props) {
  const { user } = useAuthStore();
  const isAdmin = ['ADMIN', 'ADMINISTRATOR'].includes(user?.role || '');
  const [connectors, setConnectors] = useState<ConnectorDef[]>([]);
  const [loading, setLoading]       = useState(true);
  const [configuringId, setConfiguringId] = useState<string | null>(null);
  const [configValues, setConfigValues]   = useState<Record<string, string>>({});
  const [saving, setSaving]           = useState(false);
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});

  useEffect(() => {
    getConnectors().then(data => {
      setConnectors(data);
      // Auto-correct selection: clear any unconfigured selections
      const corrected = { ...selection };
      let changed = false;
      for (const [slot, connId] of Object.entries(selection) as [keyof ConnectorSelection, string][]) {
        if (slot === 'webhook' || !connId || connId === 'none') continue;
        const def = data.find(c => c.id === connId);
        if (def && !def.isConfigured) {
          corrected[slot] = '';
          changed = true;
        }
      }
      if (changed) onChange(corrected);
    }).finally(() => setLoading(false));
  }, []);

  const byCategory = CATEGORY_ORDER.reduce((acc, cat) => {
    acc[cat] = connectors.filter(c => c.category === cat);
    return acc;
  }, {} as Record<string, ConnectorDef[]>);

  const handleSave = async (connector: ConnectorDef) => {
    setSaving(true);
    try {
      await saveConnectorConfig(connector.id, configValues, connector.label);
      setConnectors(prev => prev.map(c =>
        c.id === connector.id ? { ...c, isConfigured: true, isActive: true } : c
      ));
      // Auto-select this connector now that it's configured
      const slot = CATEGORY_TO_SLOT[connector.category];
      if (slot) onChange({ ...selection, [slot]: connector.id });
      setConfiguringId(null);
      setConfigValues({});
    } catch (e: any) {
      alert(`Save failed: ${e.response?.data?.error || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (connector: ConnectorDef) => {
    await deleteConnectorConfig(connector.id);
    setConnectors(prev => prev.map(c =>
      c.id === connector.id ? { ...c, isConfigured: false, isActive: false } : c
    ));
    // Clear selection if this was selected
    const slot = CATEGORY_TO_SLOT[connector.category];
    if (slot && selection[slot] === connector.id) {
      onChange({ ...selection, [slot]: '' });
    }
  };

  const selectConnector = (connector: ConnectorDef) => {
    if (!connector.isConfigured) {
      setConfiguringId(connector.id);
      setConfigValues({});
      return;
    }
    const slot = CATEGORY_TO_SLOT[connector.category];
    if (slot) {
      const isCurrentlySelected = selection[slot] === connector.id;
      onChange({ ...selection, [slot]: isCurrentlySelected ? '' : connector.id });
    }
  };

  if (loading) return (
    <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" />Loading connectors...
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Zap className="h-4 w-4 text-primary" />
        <h3 className="text-sm font-semibold">Data Sources</h3>
        <span className="text-xs text-muted-foreground">— select one per stage</span>
      </div>

      {CATEGORY_ORDER.map(cat => {
        const options = byCategory[cat] || [];
        if (!options.length) return null;
        const slot     = CATEGORY_TO_SLOT[cat];
        const selected = selection[slot];

        return (
          <div key={cat} className="space-y-1.5">
            <div className="flex items-start gap-2">
              <div className="flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 text-primary text-xs font-bold shrink-0 mt-0.5">
                {PIPELINE_STAGE[cat]?.step}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-foreground">
                  {PIPELINE_STAGE[cat]?.icon} {PIPELINE_STAGE[cat]?.label}
                  <span className="ml-2 text-[10px] font-normal text-primary bg-primary/10 px-1.5 py-0.5 rounded-full">
                    {PIPELINE_STAGE[cat]?.agentName}
                  </span>
                </p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{PIPELINE_STAGE[cat]?.desc}</p>
                <p className="text-[10px] text-primary/70 font-medium">→ feeds {PIPELINE_STAGE[cat]?.abmStages}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {options.map(connector => {
                const isSelected    = selected === connector.id;
                const isConfiguring = configuringId === connector.id;

                return (
                  <div key={connector.id} className="relative">
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => selectConnector(connector)}
                      onKeyDown={e => e.key === 'Enter' && selectConnector(connector)}
                      title={!connector.isConfigured ? 'Click the ⚙ gear to add API key first' : connector.description}
                      className={`
                        flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all
                        ${isSelected
                          ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                          : connector.isConfigured
                          ? 'bg-card border-border hover:border-primary/50 text-foreground cursor-pointer'
                          : 'bg-muted/30 border-dashed border-border/50 text-muted-foreground cursor-default'
                        }
                      `}
                    >
                      {isSelected
                        ? <CheckCircle className="h-3 w-3 shrink-0" />
                        : connector.isConfigured
                        ? <CheckCircle className="h-3 w-3 shrink-0 text-emerald-500" />
                        : <Lock className="h-3 w-3 shrink-0 text-muted-foreground/40" />
                      }
                      {connector.label}

                      {/* Gear button — admin only */}
                      {isAdmin && (
                      <button
                        onClick={e => { e.stopPropagation(); setConfiguringId(isConfiguring ? null : connector.id); setConfigValues({}); }}
                        title="Configure API key"
                        className={`ml-0.5 p-0.5 rounded transition-opacity ${
                          isSelected ? 'text-primary-foreground/70 hover:text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        <Settings className="h-3 w-3" />
                      </button>
                      )}
                    </div>

                    {/* Inline config panel — admin only */}
                    {isAdmin && isConfiguring && (
                      <div className="absolute left-0 top-full mt-2 z-30 w-80 bg-card border border-border rounded-xl shadow-xl p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-semibold">{connector.label}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{connector.description}</p>
                          </div>
                          <button onClick={() => setConfiguringId(null)} className="text-muted-foreground hover:text-foreground ml-2 shrink-0">
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>

                        {connector.fields.map(field => (
                          <div key={field.key}>
                            <label className="block text-xs font-medium text-muted-foreground mb-1">{field.label}</label>
                            <div className="relative">
                              <input
                                type={field.type === 'password' && !showPasswords[field.key] ? 'password' : 'text'}
                                placeholder={field.placeholder}
                                value={configValues[field.key] || ''}
                                onChange={e => setConfigValues(p => ({ ...p, [field.key]: e.target.value }))}
                                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-xs pr-8 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
                              />
                              {field.type === 'password' && (
                                <button type="button"
                                  onClick={() => setShowPasswords(p => ({ ...p, [field.key]: !p[field.key] }))}
                                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                >
                                  {showPasswords[field.key] ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                                </button>
                              )}
                            </div>
                          </div>
                        ))}

                        <div className="flex gap-2 pt-1">
                          <button
                            onClick={() => handleSave(connector)}
                            disabled={saving || connector.fields.filter(f => f.type === 'password').some(f => !configValues[f.key])}
                            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 disabled:opacity-40 transition-colors"
                          >
                            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                            Save & activate
                          </button>
                          {connector.isConfigured && (
                            <button onClick={() => handleDelete(connector)}
                              className="px-3 py-2 rounded-lg border border-red-200 dark:border-red-900 text-red-500 text-xs hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors">
                              Remove
                            </button>
                          )}
                        </div>

                        {connector.docsUrl && (
                          <a href={connector.docsUrl} target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-1 text-xs text-primary hover:underline">
                            Get API key <ExternalLink className="h-2.5 w-2.5" />
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Active summary */}
      <div className="flex flex-wrap gap-1.5 pt-2 border-t border-border">
        <span className="text-xs text-muted-foreground self-center">Active:</span>
        {Object.entries(selection)
          .filter(([k, v]) => k !== 'webhook' && v && v !== 'none')
          .map(([slot, connId]) => {
            const conn = connectors.find(c => c.id === connId);
            return conn ? (
              <span key={slot} className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                ✓ {conn.label}
              </span>
            ) : null;
          })}
        {Object.entries(selection).filter(([k, v]) => k !== 'webhook' && v && v !== 'none').length === 0 && (
          <span className="text-xs text-amber-600 dark:text-amber-400">No connectors selected — add API keys and select one per stage</span>
        )}
        {/* Show which required stages are missing */}
        {(['discovery','llm'] as const).filter(slot => !selection[slot]).map(slot => (
          <span key={slot} className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
            ⚠ {slot === 'discovery' ? 'Discovery (required)' : 'AI/LLM (required)'} not set
          </span>
        ))}
      </div>
    </div>
  );
}
